<script setup lang="ts">
import { computed } from "vue";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import { formLabelCva, formLabelRowCva, formRequiredCva } from "./form.cva";

const {
  label = "",
  required = false,
  layout = "is-column",
  display = "block",
  for: htmlFor = undefined,
  help = "",
  labelId = undefined,
} = defineProps<{
  label?: string
  required?: boolean
  layout?: "is-column" | "is-row"
  /** `inline` inside a component's own flex column, `block` directly in page markup (see formLabelCva). */
  display?: "inline" | "block"
  /** Mirrors the native `for` attribute; omit for controls that own their own label. */
  for?: string
  /** Help text behind a question mark after the label. Never shown as a line under the field. */
  help?: string
  /** Id of the label element itself, for `aria-labelledby`; with help it is not on the wrapper, so the name leaves out the button. */
  labelId?: string
}>();

const labelClasses = computed(() => formLabelCva({ layout, display }));
// With help the row carries the label metrics (margin, row width) and the label itself only the text.
const rowClasses = computed(() => formLabelRowCva({ layout }));
const innerLabelClasses = computed(() => formLabelCva({ layout: "is-column", display, flush: true }));
const requiredClasses = computed(() => formRequiredCva());
</script>

<template>
  <div v-if="help" :class="rowClasses">
    <!-- The question mark sits next to the label, not inside it: a button may not live in a <label>. -->
    <label :id="labelId" :class="innerLabelClasses" :for="htmlFor">
      <slot>{{ label }}</slot>
      <span v-if="required" :class="requiredClasses">*</span>
    </label>
    <VHelpTooltip :content="help" :subject="label" />
  </div>
  <label v-else :id="labelId" :class="labelClasses" :for="htmlFor">
    <slot>{{ label }}</slot>
    <span v-if="required" :class="requiredClasses">*</span>
  </label>
</template>
